import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { BasketAddItemOptions, BasketAmendItemOptions, BasketResponse } from 'types/BasketTypes';
import { Logger } from 'utilities/loggerv2/logger';
import { DEFAULT_BASKET_ERROR_MESSAGE } from 'constants/basket';
import { BasketServiceInterface } from './interface/BasketServiceInterface';
import { startSpan, endSpan, failSpan } from '../../utilities/loggerv2/performanceSpans';

export default class BasketService {
  private basketService: BasketServiceInterface;

  private logger: Promise<Logger>;

  constructor(basketService: BasketServiceInterface) {
    this.basketService = basketService;
    this.logger = LoggerFactory.getLogger('Basket service');
  }

  async addItem(options: BasketAddItemOptions): Promise<BasketResponse> {
    const span = startSpan('addToBasket', {
      productSku: options.item.variantId,
      hasPersonalisation: !!options.item.personalisation,
      hasWarranty: !!options.item.warrantyOptionNumber,
      hasAddons: !!(options.item.selectedAddOnServices && options.item.selectedAddOnServices.length > 0),
    });

    try {
      const response = await this.basketService.addItem(options);

      if (!response.success) {
        (await this.logger).logWithCode(ErrorCodes.BASKET_ADD_ITEM_FAILED, new Error(response.message));

        failSpan(span, {
          errorCode: response.errorCode || 'unknown_error',
          errorType: ErrorCodes.BASKET_ADD_ITEM_FAILED.code,
          message: response.message,
          status: response.status,
        });
      } else {
        endSpan(span, {
          success: true,
        });
      }

      return response;
    } catch (error) {
      (await this.logger).logWithCode(ErrorCodes.BASKET_ADD_ITEM_FAILED, error);

      failSpan(span, {
        errorCode: 'unknown_error',
        errorType: ErrorCodes.BASKET_ADD_ITEM_FAILED.code,
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        status: 500,
      });

      return {
        message: DEFAULT_BASKET_ERROR_MESSAGE,
        status: 500,
        success: false,
      };
    }
  }

  async amendItem(options: BasketAmendItemOptions): Promise<BasketResponse> {
    const span = startSpan('amendBasket', {
      productSku: options.item.variantId,
      hasPersonalisation: !!options.item.personalisation,
      hasWarranty: !!options.item.warrantyOptionNumber,
      hasAddons: !!(options.item.selectedAddOnServices && options.item.selectedAddOnServices.length > 0),
    });

    try {
      const response = await this.basketService.amendItem(options);

      if (!response.success) {
        (await this.logger).logWithCode(ErrorCodes.BASKET_AMEND_ITEM_FAILED, new Error(response.message));

        failSpan(span, {
          errorCode: response.errorCode || 'unknown_error',
          errorType: ErrorCodes.BASKET_AMEND_ITEM_FAILED.code,
          message: response.message,
          status: response.status,
        });
      } else {
        endSpan(span, {
          success: true,
        });
      }

      return response;
    } catch (error) {
      (await this.logger).logWithCode(ErrorCodes.BASKET_AMEND_ITEM_FAILED, error);

      failSpan(span, {
        errorCode: 'unknown_error',
        errorType: ErrorCodes.BASKET_AMEND_ITEM_FAILED.code,
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        status: 500,
      });

      return {
        message: DEFAULT_BASKET_ERROR_MESSAGE,
        status: 500,
        success: false,
      };
    }
  }
}
